import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Pagination, Table } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { adminApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import {
  buildPageItems,
  paginateItems,
} from "../../utils/adminOrders";
import {
  ENQUIRY_PAGE_SIZES,
  ENQUIRY_SORT_OPTIONS,
  filterAndSortEnquiries,
} from "../../utils/adminEnquiries";
import { TableSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const CONTACT_STATUSES = ["new", "in_progress", "resolved", "closed"];

const STATUS_VARIANT = {
  new: "warning",
  in_progress: "primary",
  resolved: "success",
  closed: "dark",
};

const DEFAULT_FILTERS = {
  search: "",
  status: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

const ManageEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.enquiries();
      setEnquiries(res.data.data.enquiries || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load enquiries"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredEnquiries = useMemo(
    () => filterAndSortEnquiries(enquiries, filters),
    [enquiries, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredEnquiries, page, pageSize),
    [filteredEnquiries, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.status !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const changeStatus = async (enquiry, status) => {
    setSavingId(enquiry._id);
    try {
      const res = await adminApi.updateEnquiry(enquiry._id, { status });
      const updated = res.data.data.enquiry;
      setEnquiries((prev) =>
        prev.map((e) => (e._id === updated._id ? { ...e, status: updated.status } : e))
      );
      toast.success("Enquiry updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update enquiry"));
    } finally {
      setSavingId(null);
    }
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Contact enquiries</h1>
          <p>Search, filter, and follow up on contact form messages.</p>
        </div>
        <span className="manage-count">
          {filteredEnquiries.length === enquiries.length
            ? `${enquiries.length} total`
            : `${filteredEnquiries.length} of ${enquiries.length}`}
        </span>
      </header>

      {!loading && enquiries.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search name, email, phone, message…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="all">All statuses</option>
            {CONTACT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
          />
          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {ENQUIRY_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {ENQUIRY_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} cols={4} />
      ) : enquiries.length === 0 ? (
        <p className="manage-empty">No enquiries yet.</p>
      ) : filteredEnquiries.length === 0 ? (
        <div className="manage-empty">
          <p>No enquiries match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table align-middle mb-0">
              <thead>
                <tr>
                  <th>From</th>
                  <th>Message</th>
                  <th>Received</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((enquiry) => {
                  const isExpanded = expandedId === enquiry._id;
                  return (
                    <React.Fragment key={enquiry._id}>
                      <tr>
                        <td>
                          <strong>{enquiry.name}</strong>
                          <div className="manage-muted">{enquiry.email}</div>
                          <div className="manage-muted">{enquiry.phone}</div>
                          <Button
                            variant="link"
                            size="sm"
                            className="manage-expand-btn"
                            onClick={() =>
                              setExpandedId(isExpanded ? null : enquiry._id)
                            }
                          >
                            {isExpanded ? (
                              <>
                                <FaChevronUp /> Hide
                              </>
                            ) : (
                              <>
                                <FaChevronDown /> Details
                              </>
                            )}
                          </Button>
                        </td>
                        <td className="manage-message">
                          {(enquiry.message || "").slice(0, 120)}
                          {(enquiry.message || "").length > 120 ? "…" : ""}
                        </td>
                        <td>{new Date(enquiry.createdAt).toLocaleDateString()}</td>
                        <td className="text-nowrap">
                          <Badge
                            bg={STATUS_VARIANT[enquiry.status] || "secondary"}
                            className="me-2"
                          >
                            {enquiry.status}
                          </Badge>
                          <Form.Select
                            size="sm"
                            className="manage-select"
                            value={enquiry.status}
                            disabled={savingId === enquiry._id}
                            onChange={(e) => changeStatus(enquiry, e.target.value)}
                          >
                            {CONTACT_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="manage-order-details-row">
                          <td colSpan={4}>
                            <div className="manage-details-panel">
                              <p className="mb-2">
                                <strong>Full message</strong>
                              </p>
                              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                {enquiry.message || "—"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {pagination.showingFrom}–{pagination.showingTo} of{" "}
              {pagination.total} enquiries
            </p>
            {pagination.totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setExpandedId(null);
                  }}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => {
                        setPage(item);
                        setExpandedId(null);
                      }}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(pagination.totalPages, p + 1));
                    setExpandedId(null);
                  }}
                />
              </Pagination>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManageEnquiries;
